/**
 * Everything told to Google Analytics goes through here (see docs/analytics.md).
 *
 * There are two destinations: Orderswift's own GA4 property, which every ordering site reports to, and the operator's, if they have given
 * a GA4 measurement ID (`G-...`) or a Tag Manager container (`GTM-...`). An event goes to both unless it says otherwise.
 *
 * Nothing is sent for a visitor with Do Not Track or Global Privacy Control on. The operator is not known until its fetch has finished,
 * so events for the operator wait for it, and are sent (or dropped, if there is no property to send them to) when it comes.
 */

import { operatorName } from '../config/operator';

export const ORDERSWIFT_GA_ID = 'G-EJKV1FTCSC';

type Params = Record<string, unknown>;

type OperatorAnalytics = {
    /** the GA4 measurement ID or the GTM container, if the operator has one */
    id: string | null;
    ccy?: string;
};

type TrackOptions = {
    /** 'orderswift' keeps an event to Orderswift's own property; the default is both */
    to?: 'both' | 'orderswift';
    /** GA4 ecommerce fields (currency, value, items...): flat for gtag, in an `ecommerce` object for Tag Manager */
    ecommerce?: Params;
};

const VARIANT_KEY = 'site_select_variant';
const MAX_WAITING = 50;

let operator: OperatorAnalytics | null = null;
let waiting: Array<(operator: OperatorAnalytics) => void> = [];
let variant: string | null = null;

const configured = new Set<string>();
const sent = new Set<string>();

/** False for a visitor who asked not to be tracked (Do Not Track, Global Privacy Control). */
export const trackingAllowed = (): boolean => navigator.doNotTrack !== '1' && !navigator.globalPrivacyControl;

const gtag = (): ((...args: unknown[]) => void) | null => {
    const fn = (window as any).gtag;

    return typeof fn === 'function' ? fn : null;
};

const isTagManager = (id: string) => id.startsWith('GTM-');

const loadTagManager = (id: string) => {
    if (configured.has(id)) return;
    configured.add(id);

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });

    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(id)}`;
    document.head.appendChild(script);
};

/** Tells analytics who the operator is, once it is known: sets up their property and sends what was waiting for it. */
export const setOperatorAnalytics = (info: { google_analytics?: string | null; ccy?: string }) => {
    const id = trackingAllowed() ? info.google_analytics?.trim() || null : null;

    operator = { id, ccy: info.ccy };

    if (id) {
        if (isTagManager(id)) {
            loadTagManager(id);
        } else if (!configured.has(id)) {
            configured.add(id);
            // (the page views are sent by hand, as the route changes)
            gtag()?.('config', id, { send_page_view: false });
        }
    }

    const queued = waiting;
    waiting = [];
    queued.forEach((send) => send(operator as OperatorAnalytics));
};

/** The way into the site chooser a visitor was given, kept for the visit so what they go on to do can be compared (see docs/analytics.md). */
export const rememberSiteSelectVariant = (value: 'page' | 'modal') => {
    variant = value;

    try {
        sessionStorage.setItem(VARIANT_KEY, value);
    } catch {
        // (storage is off: it is kept for as long as the page is open)
    }
};

const rememberedSiteSelectVariant = (): string | null => {
    if (variant) return variant;

    try {
        return sessionStorage.getItem(VARIANT_KEY);
    } catch {
        return null;
    }
};

/** True the first time it is asked for a key, and false after (also after a reload), so that something is sent once. */
export const firstTime = (key: string): boolean => {
    const storageKey = `analytics_sent_${key}`;

    if (sent.has(storageKey)) return false;
    sent.add(storageKey);

    try {
        if (localStorage.getItem(storageKey)) return false;
        localStorage.setItem(storageKey, '1');
    } catch {
        // (storage is off: once while the page is open)
    }

    return true;
};

const compact = (params: Params): Params =>
    Object.fromEntries(Object.entries(params).filter(([, value]) => value !== undefined && value !== null));

const defaults = (): Params => compact({
    operator_handle: operatorName,
    site_select_variant: rememberedSiteSelectVariant(),
});

const sendToOperator = (operator: OperatorAnalytics, name: string, data: Params, ecommerce?: Params) => {
    if (!operator.id) return;

    const fields = ecommerce ? compact({ currency: operator.ccy, ...ecommerce }) : undefined;

    if (isTagManager(operator.id)) {
        window.dataLayer = window.dataLayer || [];

        if (fields) {
            // (clear the last ecommerce object first, as Google advises)
            window.dataLayer.push({ ecommerce: null });
            window.dataLayer.push({ event: name, ecommerce: fields, ...data });
        } else {
            window.dataLayer.push({ event: name, ...data });
        }

        return;
    }

    gtag()?.('event', name, { ...data, ...fields, send_to: operator.id });
};

/** Tells Google Analytics of an event (to Orderswift's property, and to the operator's unless told otherwise). */
export const track = (name: string, params: Params = {}, { to = 'both', ecommerce }: TrackOptions = {}) => {
    if (!trackingAllowed()) return;

    const data = { ...defaults(), ...compact(params) };

    const fields = ecommerce ? compact({ currency: operator?.ccy, ...ecommerce }) : undefined;
    gtag()?.('event', name, { ...data, ...fields, send_to: ORDERSWIFT_GA_ID });

    if (to !== 'both') return;

    if (operator) {
        sendToOperator(operator, name, data, ecommerce);
    } else if (waiting.length < MAX_WAITING) {
        waiting.push((known) => sendToOperator(known, name, data, ecommerce));
    }
};

/** A page view, with the address as it is now (the operator's may be sent later, when the visitor has moved on). */
export const trackPageView = (path: string) =>
    track('page_view', { page_location: window.location.href, page_path: path, page_title: document.title });

/** A basket line, or a menu item, as a GA4 item. Prices are in pounds, not pence. */
export type LineLike = {
    uid: string;
    name: string;
    price: number;
    modifiersTotal?: number;
    quantity?: number;
    category?: { name?: string } | null;
};

export const toItems = (lines: LineLike[], quantity?: number) =>
    lines.map((line) => ({
        item_id: line.uid,
        item_name: line.name,
        item_category: line.category?.name,
        price: (line.price + (line.modifiersTotal ?? 0)) / 100,
        quantity: quantity ?? line.quantity ?? 1,
    }));

/** What a set of lines comes to, in pounds. */
export const valueOf = (lines: LineLike[], quantity?: number) =>
    lines.reduce(
        (total, line) => total + ((line.price + (line.modifiersTotal ?? 0)) * (quantity ?? line.quantity ?? 1)) / 100,
        0
    );

/** The lines of an order that has been made, as GA4 items: a line's own total is what was charged for it, whatever came off. */
export const toOrderItems = (lines: Array<LineLike & { item_total?: number }>) =>
    lines.map((line) => {
        const quantity = Math.max(line.quantity ?? 1, 1);
        const total = line.item_total ?? (line.price + (line.modifiersTotal ?? 0)) * quantity;

        return {
            item_id: line.uid,
            item_name: line.name,
            item_category: line.category?.name,
            price: total / quantity / 100,
            quantity,
        };
    });
